"use client";

import { useEffect, useRef, useState } from "react";

function TeamPhoto({ src, alt }: { src: string; alt: string }) {
  const [errored, setErrored] = useState(false);
  return (
    <img
      src={errored ? "/assets/images/no_image.png" : src}
      alt={alt}
      onError={() => setErrored(true)}
      className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
    />
  );
}

const team = [
  {
    name: "Lory Trevizo",
    role: "Management, Finance & Tax Expert",
    specialty: "Licensing & Compliance",
    bio: "Lory oversees the financial and operational backbone of Premier Property MGT. She brings expertise in property management finance, tax strategy, and the licensing and regulatory compliance required to operate short-term rentals in Florida — ensuring your portfolio is always protected, properly structured, and fully above board.",
    photo: "/assets/images/team/lory-trevizo.jpg",
    icon: (
      <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    name: "Richard Franco",
    role: "Operations Manager",
    specialty: "Maintenance, Website & Social Media",
    bio: "Richard brings hands-on property management experience combined with direct maintenance oversight and full command of our digital presence. He is the driving force behind our website and social media channels — the same platforms that actively generate bookings and promote listings for the properties we manage.",
    photo: "/assets/images/team/richard-franco.jpg",
    icon: (
      <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    name: "Daniel Franco",
    role: "Marketing Director",
    specialty: "Former Airbnb Cleaning Company Owner",
    bio: "Daniel leads our marketing strategy with a background that uniquely spans both guest-facing promotion and the operational realities of short-term rental turnover. As the former owner of an Airbnb-focused cleaning company, he understands precisely what guests expect to find upon arrival — and how to market that experience to attract premium bookings.",
    photo: "/assets/images/team/daniel-franco.jpg",
    icon: (
      <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    ),
  },
];

export default function TeamSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("revealed");
        });
      },
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal-hidden text-center mb-16">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">
            The People Behind Premier
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-5 leading-tight">
            Meet{" "}
            <span className="italic font-light text-gradient-gold">Our Team</span>
          </h2>
          <p className="text-navy/55 text-base max-w-xl mx-auto leading-relaxed">
            Local experts who live and work in Panama City Beach — personally invested in your property&apos;s success.
          </p>
        </div>

        {/* Team Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {team.map((member, index) => (
            <div
              key={member.name}
              className="reveal-hidden group"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="bg-sand rounded-3xl overflow-hidden shadow-sm hover:shadow-luxury transition-shadow duration-300 h-full flex flex-col">
                {/* Photo */}
                <div className="relative h-80 overflow-hidden">
                  <TeamPhoto src={member.photo} alt={`${member.name} — ${member.role} at Premier Property MGT`} />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
                  {/* Specialty badge */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-flex items-center gap-1.5 bg-gold/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      {member.icon}
                      {member.specialty}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-heading text-2xl text-navy mb-1">{member.name}</h3>
                  <p className="text-gold text-sm font-semibold uppercase tracking-wide mb-4">
                    {member.role}
                  </p>
                  <p className="text-navy/60 text-sm leading-relaxed flex-1">{member.bio}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
